import React, { useState, useRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import SortableTableHeader from './SortableTableHeader';

// Импортируем функции из нового API
import {
  getColumnByKey,
  getColumnLabel,
  calculateColumnValue,
  isPercentageColumn,
  isSystemColumn
} from './columnsAPI';

// Импортируем обновленные утилиты
import { formatNumber, formatColumnValue } from './utils';

// Высота фиксированного StatisticsFilters в пикселях
const FILTERS_HEIGHT = 70; 

// Компоненты иконок для сортировки
const ArrowUp = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ArrowDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ArrowUpDown = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
  </svg>
);

// Функция для получения иконки сортировки
const getSortIcon = (columnKey, sortConfig) => {
  if (!sortConfig || sortConfig.key !== columnKey) {
    return <ArrowUpDown className="w-4 h-4 opacity-50" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ArrowUp className="w-4 h-4" /> 
    : <ArrowDown className="w-4 h-4" />;
};

const StatisticsTable = ({
  statistics,
  visibleColumns,
  columnOrder,
  columnsMetadata = [], // Получаем метаданные вместо columns
  setColumnOrder,
  setVisibleColumns,
  updateQueryParams,
  selectedDomain,
  selectedLandings,
  selectedCountry,
  selectedDateRange,
  selectedTrafficSource
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'asc' });
  const tableRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableOffsetTop, setTableOffsetTop] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);
  const [columnWidths, setColumnWidths] = useState({});
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: (event) => {
        const table = event.currentTarget.querySelector('table');
        if (!table) return { x: 0, y: 0 };
        
        const headers = Array.from(table.querySelectorAll('th'));
        const index = headers.findIndex(node => node.contains(event.target));
        
        if (index === -1) return { x: 0, y: 0 };
        
        const rect = headers[index].getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }
    })
  );

  // Измеряем ширину таблицы и позицию
  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        setHeaderWidth(tableRef.current.offsetWidth);
        setTableOffsetTop(tableRef.current.getBoundingClientRect().top + window.scrollY);
        setTableHeight(tableRef.current.offsetHeight);
        
        const headers = tableRef.current.querySelectorAll('thead th');
        const widths = {};
        headers.forEach((header) => {
          const columnId = header.getAttribute('data-column-id');
          if (columnId) {
            widths[columnId] = header.offsetWidth;
          }
        });
        setColumnWidths(widths);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [statistics, visibleColumns]);

  // Обработчик скролла для определения, когда заголовок должен стать фиксированным
  useEffect(() => {
    const handleScroll = () => {
      if (tableRef.current) {
        const tableTop = tableRef.current.getBoundingClientRect().top;
        const tableBottom = tableRef.current.getBoundingClientRect().bottom;
        
        setIsHeaderSticky(tableTop < FILTERS_HEIGHT && tableBottom > FILTERS_HEIGHT);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Обработчик сортировки
  const handleSort = (columnId) => {
    let direction = 'asc';
    if (sortConfig.key === columnId) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key: columnId, direction });
  };

  // Универсальная функция сортировки на основе метаданных
  const getSortValue = (stat, columnId) => {
    const column = getColumnByKey(columnId, columnsMetadata);
    
    if (!column) {
      // Fallback для неизвестных колонок
      return stat[columnId] || 0;
    }

    // Для даты используем объект Date
    if (columnId === 'date') {
      return new Date(stat.date);
    }

    // Для всех остальных колонок используем универсальную функцию расчета
    return calculateColumnValue(columnId, stat, columnsMetadata);
  };

  // Сортировка данных
  const sortedData = React.useMemo(() => {
    if (!statistics || !statistics.daily_stats) return [];
    
    const sortableData = [...statistics.daily_stats];
    if (sortConfig.key === null) return sortableData;
    
    return sortableData.sort((a, b) => {
      const aValue = getSortValue(a, sortConfig.key);
      const bValue = getSortValue(b, sortConfig.key);
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [statistics, sortConfig, columnsMetadata]);

  // Обработчик перетаскивания колонок
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active && over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
        setColumnOrder(newOrder);
        
        updateQueryParams({ 
          visibleColumns,
          columnOrder: newOrder,
          domain: selectedDomain,
          landings: selectedLandings,
          country: selectedCountry,
          dateRange: selectedDateRange,
          trafficSource: selectedTrafficSource
        });
      }
    }
  };

  // Универсальная функция рендеринга ячейки
  const renderCell = (stat, colId) => {
    const column = getColumnByKey(colId, columnsMetadata);
    
    // Специальная обработка для даты
    if (colId === 'date') {
      let cellContent;
      try {
        cellContent = format(new Date(stat.date), 'dd.MM.yyyy');
      } catch (e) {
        console.error('Error formatting date:', stat.date, e);
        cellContent = 'Invalid date';
      }
      
      return (
        <td 
          key={colId} 
          className="px-6 py-4 whitespace-nowrap text-sm border-r border-gray-200 last:border-r-0 text-gray-600"
          style={{ width: columnWidths[colId] ? `${columnWidths[colId]}px` : 'auto' }}
        >
          {cellContent}
        </td>
      );
    }

    // Для всех остальных колонок используем универсальную логику
    let value;
    let isPercentage = false;
    
    if (column) {
      // Используем метаданные для расчета значения
      value = calculateColumnValue(colId, stat, columnsMetadata);
      isPercentage = isPercentageColumn(colId, columnsMetadata);
    } else {
      // Fallback для неизвестных колонок
      console.warn(`Column ${colId} not found in metadata, using fallback`);
      value = stat[colId] || 0;
    }

    // Форматируем значение
    const formattedValue = isPercentage 
      ? `${value}%` 
      : (typeof value === 'number' ? formatNumber(value) : value);

    return (
      <td 
        key={colId} 
        className={`px-6 py-4 whitespace-nowrap text-sm border-r border-gray-200 last:border-r-0 
          ${isPercentage ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}
        style={{ width: columnWidths[colId] ? `${columnWidths[colId]}px` : 'auto' }}
      >
        {formattedValue}
      </td>
    );
  };

  // Универсальная функция рендеринга ячейки со средними значениями (ИСПРАВЛЕННАЯ)
  const renderAverageCell = (colId) => {
    if (!statistics || !statistics.daily_stats || statistics.daily_stats.length === 0) {
      return <td key={colId}></td>;
    }
    
    const dataLength = statistics.daily_stats.length;
    const column = getColumnByKey(colId, columnsMetadata);
    
    // Специальная обработка для даты
    if (colId === 'date') {
      return (
        <td 
          key={colId} 
          className="px-6 py-4 whitespace-nowrap text-sm border-r border-gray-200 last:border-r-0 text-gray-700 font-medium"
          style={{ width: columnWidths[colId] ? `${columnWidths[colId]}px` : 'auto' }}
        >
          Average
        </td>
      );
    }

    let averageContent;
    let isPercentage = false;
    
    if (column) {
      isPercentage = isPercentageColumn(colId, columnsMetadata);
      
      if (isPercentage) {
        // ИСПРАВЛЕНИЕ: Для процентных колонок используем ИСХОДНЫЕ ДАННЫЕ
        // Вычисляем общий процент из сумм числителей и знаменателей
        const config = column.calculation_config || {};
        let totalNumerator = 0;
        let totalDenominator = 0;
        
        statistics.daily_stats.forEach(stat => {
          if (column.column_type === 'percentage_ratio') {
            // percentage_ratio
            const numeratorValue = calculateColumnValue(config.numerator, stat, columnsMetadata);
            const denominatorValue = calculateColumnValue(config.denominator, stat, columnsMetadata);
            totalNumerator += numeratorValue;
            totalDenominator += denominatorValue;
          } else if (column.column_type === 'percentage_attempts') {
            // percentage_attempts
            const targetValue = calculateColumnValue(config.target, stat, columnsMetadata);
            
            // Для total используем config.total_columns
            const totalColumns = config.total_columns || [];
            const statTotal = totalColumns.reduce((sum, col) => {
              return sum + calculateColumnValue(col, stat, columnsMetadata);
            }, 0);
            
            totalNumerator += targetValue;
            totalDenominator += statTotal;
          }
        });
        
        const avgPercentage = totalDenominator > 0 
          ? ((totalNumerator / totalDenominator) * 100).toFixed(1) 
          : '0';
        averageContent = `${avgPercentage}%`;
      } else {
        // Для обычных колонок вычисляем среднее арифметическое
        const total = statistics.daily_stats.reduce((sum, stat) => {
          const value = calculateColumnValue(colId, stat, columnsMetadata);
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        averageContent = formatNumber(Math.round(total / dataLength));
      }
    } else {
      // Fallback для неизвестных колонок
      const total = statistics.daily_stats.reduce((sum, stat) => sum + (stat[colId] || 0), 0);
      averageContent = formatNumber(Math.round(total / dataLength));
    }

    return (
      <td 
        key={colId} 
        className={`px-6 py-4 whitespace-nowrap text-sm border-r border-gray-200 last:border-r-0
          ${isPercentage ? 'text-blue-500' : 'text-gray-700'} font-medium`}
        style={{ width: columnWidths[colId] ? `${columnWidths[colId]}px` : 'auto' }}
      >
        {averageContent}
      </td>
    );
  };

  // Создаем список видимых колонок с учетом порядка
  const getVisibleColumnIds = useCallback(() => {
    return columnOrder.filter(colId => visibleColumns.includes(colId));
  }, [columnOrder, visibleColumns]);

  // Если данных нет, возвращаем пустой компонент
  if (!statistics || !statistics.daily_stats || statistics.daily_stats.length === 0) {
    return <div className="text-center py-8 text-gray-500">No data available</div>;
  }

  // Получаем видимые колонки с учетом порядка
  const visibleColumnIds = getVisibleColumnIds();

  return (
    <div className="relative">
      {/* Фиксированный заголовок - появляется только при прокрутке */}
      {isHeaderSticky && (
        <div 
          className="fixed z-20 overflow-hidden bg-white shadow-md"
          style={{ 
            width: headerWidth ? `${headerWidth}px` : '100%',
            top: `${FILTERS_HEIGHT}px`,
            left: tableRef.current ? tableRef.current.getBoundingClientRect().left : 0
          }}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                {visibleColumnIds.map(colId => {
                  const columnLabel = getColumnLabel(colId, columnsMetadata);
                  
                  return (
                    <th 
                      key={`sticky-${colId}`}
                      className="px-6 py-3 border-b border-r border-gray-200 last:border-r-0 bg-gray-50"
                      style={{ 
                        width: columnWidths[colId] ? `${columnWidths[colId]}px` : 'auto' 
                      }}
                      data-column-id={colId}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex-grow cursor-pointer">
                          {columnLabel}
                        </span>
                        <div 
                          className="cursor-pointer p-1 hover:bg-gray-200 rounded"
                          onClick={() => handleSort(colId)}
                        >
                          {getSortIcon(colId, sortConfig)}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* Основная таблица */}
      <div className="overflow-x-auto border border-gray-200 rounded-lg shadow" ref={tableRef}>
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragEnd={handleDragEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableContext 
                  items={visibleColumnIds} 
                  strategy={horizontalListSortingStrategy}
                >
                  {visibleColumnIds.map((colId, index) => {
                    const columnLabel = getColumnLabel(colId, columnsMetadata);
                    
                    return (
                      <SortableTableHeader 
                        key={colId} 
                        id={colId}
                        label={columnLabel}
                        onSort={handleSort}
                        sortConfig={sortConfig}
                        isLast={index === visibleColumnIds.length - 1}
                        columnsMetadata={columnsMetadata} // Передаем метаданные для тултипов
                      />
                    );
                  })}
                </SortableContext>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {sortedData.map((stat, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  {visibleColumnIds.map(colId => renderCell(stat, colId))}
                </tr>
              ))}
              
              {/* Average row */}
              <tr className="bg-blue-50 font-medium border-t border-gray-200">
                {visibleColumnIds.map(colId => renderAverageCell(colId))}
              </tr>
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
};

export default StatisticsTable;